import { Controller } from "@hotwired/stimulus";

// Generic bulk-selection controller for any list view (contacts, etudes, …).
//
// Markup contract:
//   - Root element carries `data-controller="bulk-actions"` and a few values:
//       data-bulk-actions-resource-value="contacts" (informational)
//   - A "select all" checkbox has data-bulk-actions-target="masterCheckbox"
//   - Each row carries a checkbox `data-bulk-actions-target="rowCheckbox"`
//     with `data-id` set to the resource id.
//   - The floating action bar uses `data-bulk-actions-target="bar"` and is
//     hidden by default. Inside it, a `data-bulk-actions-target="count"`
//     element is auto-updated, and forms with class `csm_bulk-form` get an
//     auto-injected hidden field bag of selected ids on submit.
//   - Trigger buttons that open contextual modals can declare
//     `data-bulk-action="<modalId>"` — when clicked, every modal on the page
//     gets its `<input data-bulk-ids-target>` populated with the current
//     selection so the modal's form ships the correct payload.
export default class extends Controller {
  static targets = ["masterCheckbox", "rowCheckbox", "bar", "count"];
  static values  = { resource: String };

  connect() {
    this._refresh();
    // Submitter for any form tagged `csm_bulk-form` injects ids[] on submit.
    this._formSubmitHandler = (e) => this._injectIdsIntoForm(e);
    document.addEventListener("submit", this._formSubmitHandler, true);
    // Modal triggers — sync ids into open targets right before the modal opens.
    this._triggerClickHandler = (e) => this._syncIdsForTrigger(e);
    document.addEventListener("click", this._triggerClickHandler, true);
  }

  disconnect() {
    document.removeEventListener("submit", this._formSubmitHandler, true);
    document.removeEventListener("click", this._triggerClickHandler, true);
  }

  // ── User actions ────────────────────────────────────────────────────

  toggleAll(event) {
    const checked = event.currentTarget.checked;
    this.rowCheckboxTargets.forEach((cb) => { cb.checked = checked; });
    this._refresh();
  }

  toggleRow() {
    this._refresh();
  }

  clear() {
    this.rowCheckboxTargets.forEach((cb) => { cb.checked = false; });
    if (this.hasMasterCheckboxTarget) this.masterCheckboxTarget.checked = false;
    this._refresh();
  }

  // ── State ───────────────────────────────────────────────────────────

  _selectedIds() {
    return this.rowCheckboxTargets
               .filter((cb) => cb.checked)
               .map((cb) => cb.dataset.id);
  }

  _refresh() {
    const ids = this._selectedIds();
    const total = this.rowCheckboxTargets.length;
    if (this.hasCountTarget) {
      this.countTarget.textContent = ids.length;
    }
    if (this.hasMasterCheckboxTarget) {
      this.masterCheckboxTarget.checked = ids.length > 0 && ids.length === total;
      this.masterCheckboxTarget.indeterminate = ids.length > 0 && ids.length < total;
    }
    if (this.hasBarTarget) {
      this.barTarget.classList.toggle("csm_bulk-bar--visible", ids.length > 0);
    }
    document.body.classList.toggle("csm_has-selection", ids.length > 0);
  }

  // ── Form / modal plumbing ───────────────────────────────────────────

  _injectIdsIntoForm(event) {
    const form = event.target.closest("form.csm_bulk-form");
    if (!form) return;
    const ids = this._selectedIds();
    // Wipe any previous bulk_ids[] inputs to avoid stale duplicates from
    // earlier interactions in the same modal session.
    form.querySelectorAll("input[data-bulk-ids-injected='1']").forEach((n) => n.remove());
    ids.forEach((id) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = "ids[]";
      input.value = id;
      input.dataset.bulkIdsInjected = "1";
      form.appendChild(input);
    });
  }

  _syncIdsForTrigger(event) {
    const trigger = event.target.closest("[data-bulk-action]");
    if (!trigger) return;
    const modalId = trigger.dataset.bulkAction;
    const modal = document.getElementById(modalId);
    if (!modal) return;
    const ids = this._selectedIds();
    // Visible counters in the modal
    modal.querySelectorAll("[data-bulk-ids-target='count']").forEach((el) => {
      el.textContent = ids.length;
    });
    // Lists of names (best-effort — read data-name from each checkbox)
    const names = this.rowCheckboxTargets
                      .filter((cb) => cb.checked)
                      .map((cb) => cb.dataset.name)
                      .filter(Boolean);
    modal.querySelectorAll("[data-bulk-ids-target='names']").forEach((el) => {
      const max = 6;
      const shown = names.slice(0, max).join(", ");
      el.textContent = names.length <= max
        ? shown
        : `${shown} (+${names.length - max})`;
    });
    // Inject hidden ids[] inputs (rebuilt fresh)
    modal.querySelectorAll("input[data-bulk-ids-injected='1']").forEach((n) => n.remove());
    modal.querySelectorAll("form[data-bulk-target-form]").forEach((form) => {
      ids.forEach((id) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = "ids[]";
        input.value = id;
        input.dataset.bulkIdsInjected = "1";
        form.appendChild(input);
      });
    });
  }
}
